import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/api/queryKeys';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useTabsStore } from '../tabs/tabsStore';
import { useShellStore } from '../model/shellStore';
import { TruncatedText } from '@/shared/ui/TruncatedText';

interface DashboardUser {
  dashboardUserId: string;
  dashboardId: string;
  dashboard?: { dashboardId: string; dashboardName: string; refreshIntervalSec?: number | null } | null;
}

/**
 * Kullanıcının dashboard'ları. Hem menü grubu hem bölüm şeridindeki sayaç aynı veriyi
 * okuyor; sorgu paylaşıldığı için ikinci bir istek çıkmıyor.
 */
export function useDashboards() {
  const userId = useAuthStore((s) => s.user?.userId ?? '');
  const { data } = useQuery({
    queryKey: queryKeys.meta.dashboards(userId),
    queryFn: () =>
      apiClient.get<DashboardUser[]>(ENDPOINTS.DASHBOARD_USERS(userId), { octo: { expectSuccess: false } }),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
  return (data ?? []).filter((d) => d.dashboard);
}

/** Menüdeki "Dashboard'lar" grubu — DashBoardUsers/{userId}. */
export function DashboardsGroup({ headless = false }: { headless?: boolean } = {}) {
  const { t } = useTranslation();
  const openCustom = useTabsStore((s) => s.openCustom);
  const setDrawer = useShellStore((s) => s.setDrawer);
  const items = useDashboards();
  if (!items.length) return null;
  return (
    <section className="mb-2">
      {/* Bölüm şeridiyle açıldığında başlık gereksiz: seçili çip zaten aynı şeyi söylüyor. */}
      {!headless ? (
        <h3 className="text-fg-subtle flex items-center gap-1 px-2 py-1 text-[11px] font-semibold tracking-wide uppercase">
          <BarChart3 className="h-3 w-3" /> {t('shell.dashboards')}
        </h3>
      ) : null}
      {items.map((d) => (
        <button
          key={d.dashboardUserId}
          type="button"
          onClick={() => {
            openCustom({
              kind: 'dashboard',
              caption: d.dashboard!.dashboardName,
              params: {
                dashboardId: d.dashboardId,
                refreshIntervalSec: d.dashboard!.refreshIntervalSec ?? 0,
              },
              uniqueKey: `dashboard:${d.dashboardId}`,
            });
            setDrawer(false);
          }}
          className="hover:bg-bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
        >
          <BarChart3 className="text-brand-600 h-4 w-4" />
          <TruncatedText text={d.dashboard!.dashboardName} className="min-w-0 flex-1" />
        </button>
      ))}
    </section>
  );
}
